// =============================================================================
// domain/edgeHealth.ts — is the edge real, durable, and which way does it cut?
// -----------------------------------------------------------------------------
// The measurable half of the Edge Health dashboard, relocated from the old
// client's `renderEdgeHealthDashboard`. Everything the panel SHOWS is decided
// here; how it looks is not, and waits on a screenshot (D164 — the source says
// what a thing computes, the screenshot says what is on screen).
//
// Most of the numbers come straight from `selectKpis`, which is already the
// canonical KPI engine for this client. What is new is small and is the part
// worth testing:
//
//   the payoff ratio · the five-branch diagnosis · the R buckets · the trend
//
// ── 🚨 THE DIAGNOSIS IS THE PRODUCT, AND ITS BRANCHES ARE NOT INTERCHANGEABLE ──
//
// Two traders with the same positive expectancy get opposite advice. One wins
// often with thin winners: fragile, because a single bad day erases weeks. The
// other wins rarely with fat winners: durable, but only if they survive the cold
// streak. Telling the first to "let winners run" and the second to "protect the
// win rate" is worse than saying nothing — each is the other's prescription.
//
// So the branch order matters and the thresholds are load-bearing:
//
//   expectancy ≤ 0                      → negative, and nothing else is asked
//   win rate ≥ 50 and payoff < 1.5      → win-rate-driven (fragile)
//   win rate < 50 and payoff ≥ 1.5      → payoff-driven (survive the streak)
//   both                                → healthy
//   neither                             → thin but positive
//
// ⚠ NEGATIVE EXPECTANCY IS CHECKED FIRST. A losing trader can still have a 60%
// win rate, and the "healthy" branch would fire on it.
//
// ── ⚠ INFINITY IS A REAL ANSWER HERE, TWICE ──
//
// Profit factor and payoff are both infinite when there are winners and no
// losers. `selectKpis` already returns `Infinity` for profit factor deliberately,
// and this file does the same for payoff rather than coercing to 0 — a period
// with no losers is not a period with no edge.
// =============================================================================

import type { Kpis, KpiTrade } from './kpis';

/** At or above this win rate, the edge leans on frequency. */
export const EDGE_WIN_DRIVEN = 50;
/** At or above this payoff, the edge leans on the size of winners. */
export const EDGE_PAYOFF_STRONG = 1.5;
/** Below this many trades with a logged risk plan, the R shape is not shown. */
export const EDGE_R_MIN_SAMPLE = 5;
/** Below this many weeks, a trend is not a trend. */
export const EDGE_WEEK_MIN = 3;
/** The trend shows at most this many weeks, most recent last. */
export const EDGE_WEEKS_SHOWN = 12;

/**
 * Average winner ÷ average loser.
 *
 * ⚠ `Infinity` when there are winners and no losers — see the header. Zero only
 * when there is nothing to divide in the first place.
 */
export function payoffRatio(avgWin: number, avgLoss: number): number {
  if (avgLoss !== 0) return avgWin / Math.abs(avgLoss);
  return avgWin > 0 ? Infinity : 0;
}

export type EdgeVerdictKind =
  | 'negative' | 'winRate' | 'payoff' | 'healthy' | 'thin';

export interface EdgeVerdict {
  kind: EdgeVerdictKind;
  /** What to DO about it. Carries no numbers, so the UI renders it verbatim. */
  prescription: string;
  /** The two facts the branch turned on, so a caller can show its working. */
  winDriven: boolean;
  payoffStrong: boolean;
  payoff: number;
}

/**
 * ⚠ THE PRESCRIPTIONS ARE THE OLD CLIENT'S, WORD FOR WORD, and none of them
 * contains a number — which is why they can live here as constants while the
 * diagnosis sentence, which is full of them, is composed by the UI.
 */
const PRESCRIPTION: Record<EdgeVerdictKind, string> = {
  negative:
    'Stop adding size. Cut back to your A+ setups only and rebuild a positive '
    + 'expectancy before you risk more per trade.',
  winRate:
    'Protect the win rate — cap daily trades so quality stays high — and let '
    + 'winners run past 1R when structure allows, to build a margin of safety '
    + 'into the payoff.',
  payoff:
    'Survive the cold streaks. Keep risk per trade small and consistent so an '
    + 'expected run of losses can’t bury you before a big winner lands — and '
    + 'never cut a winner early.',
  healthy:
    'Both legs work — the lever now is size. Scale risk per trade deliberately '
    + 'while adherence holds, and protect what’s working.',
  thin:
    'Margin is slim. Lift one leg — tighten setup selection for a better win '
    + 'rate, or hold for bigger winners — and check that commissions and '
    + 'slippage aren’t eating the edge.',
};

export function edgeVerdict(k: Pick<Kpis, 'expectancy' | 'winRate' | 'avgWin' | 'avgLoss'>): EdgeVerdict {
  const payoff = payoffRatio(k.avgWin, k.avgLoss);
  const winDriven = k.winRate >= EDGE_WIN_DRIVEN;
  const payoffStrong = payoff >= EDGE_PAYOFF_STRONG;

  // ⚠ FIRST. See the header — a losing trader can still win 60% of the time.
  let kind: EdgeVerdictKind;
  if (k.expectancy <= 0) kind = 'negative';
  else if (winDriven && !payoffStrong) kind = 'winRate';
  else if (!winDriven && payoffStrong) kind = 'payoff';
  else if (winDriven && payoffStrong) kind = 'healthy';
  else kind = 'thin';

  return { kind, prescription: PRESCRIPTION[kind], winDriven, payoffStrong, payoff };
}

// ---------------------------------------------------------------------------
// Outcome shape
// ---------------------------------------------------------------------------

export interface RBucket { label: string; count: number; tone: 'bad' | 'warn' | 'good' }

/**
 * The five buckets, in order.
 *
 * ⚠ NOT THE SAME BUCKETS AS THE OVERVIEW TAB'S R-multiple histogram, and that is
 * the old client's choice rather than an oversight. This one asks a coarser
 * question — did the winners pay for the losers — so it has five bands where the
 * histogram has more. Reusing the histogram's would change what the panel says.
 *
 * ⚠ `0–1R` IS THE WARNING BAND, not a good one: a trade that made money but less
 * than it risked is death by a thousand cuts, and colouring it green would hide
 * the whole point of the section.
 */
const BANDS: readonly { label: string; tone: RBucket['tone']; test: (r: number) => boolean }[] = [
  { label: '≤ −1R', tone: 'bad', test: (r) => r <= -1 },
  { label: '−1–0R', tone: 'bad', test: (r) => r > -1 && r < 0 },
  { label: '0–1R', tone: 'warn', test: (r) => r >= 0 && r < 1 },
  { label: '1–2R', tone: 'good', test: (r) => r >= 1 && r < 2 },
  { label: '≥ 2R', tone: 'good', test: (r) => r >= 2 },
];

export interface RShape {
  buckets: RBucket[];
  /** Trades that had a logged risk plan — the denominator, not the trade count. */
  sample: number;
  /** ⚠ NULL with no sample. Zero would read as "you average exactly break-even". */
  avgR: number | null;
  /** False below `EDGE_R_MIN_SAMPLE` — the panel says how to unlock it instead. */
  enough: boolean;
}

export function rShape(rrs: readonly (number | null | undefined)[]): RShape {
  const vals = rrs.filter((r): r is number => r != null && Number.isFinite(r));
  return {
    buckets: BANDS.map((b) => ({
      label: b.label, tone: b.tone, count: vals.filter(b.test).length,
    })),
    sample: vals.length,
    avgR: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
    enough: vals.length >= EDGE_R_MIN_SAMPLE,
  };
}

// ---------------------------------------------------------------------------
// Durability
// ---------------------------------------------------------------------------

export interface WeekPoint { weekStart: string; expectancy: number; trades: number }

/**
 * Expectancy per week — net ÷ trades, NOT net.
 *
 * ⚠ THE DIVISION IS THE POINT, and dropping it turns this chart into a different
 * claim. A week with 40 trades and a week with 3 are the same height on a net
 * chart if they made the same money; on this one the 3-trade week is far taller,
 * which is the honest reading of "how well is each trade working".
 *
 * ⚠ `weekOf` IS INJECTED, AND IT TAKES THE TRADE, not a date string.
 *
 * 🔴 It used to take the ISO date, under a comment claiming "weeks start Monday
 * everywhere in this client". Both halves were wrong: the caller was passing the
 * SUNDAY rule, and — because this signature could only ever see `t.date` — it was
 * passing the CALENDAR date, so an evening trade was bucketed into the week its
 * clock fell in rather than the week its session belongs to. A Sunday-evening ES
 * trade could land a whole week away from where beta puts it.
 *
 * ▶ Handing over the trade moves the whole date decision to the caller, which is
 * where the day-boundary preference lives and the only place that can resolve
 * it. This module still builds no dates and holds no opinion about which one is
 * right.
 */
export function weeklyExpectancy<T extends { date: string; netPnl: number | null }>(
  trades: readonly T[], weekOf: (t: T) => string | null,
): WeekPoint[] {
  const byWeek = new Map<string, { sum: number; n: number }>();
  for (const t of trades) {
    const wk = weekOf(t);
    if (!wk) continue;
    const e = byWeek.get(wk) ?? { sum: 0, n: 0 };
    e.sum += Number(t.netPnl) || 0;
    e.n += 1;
    byWeek.set(wk, e);
  }
  return [...byWeek.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, v]) => ({ weekStart, expectancy: v.sum / v.n, trades: v.n }));
}

/** The tail the trend renders. ⚠ The LAST n, so the newest week is always shown. */
export function trendWindow(weeks: readonly WeekPoint[]): WeekPoint[] {
  return weeks.slice(-EDGE_WEEKS_SHOWN);
}

// ---------------------------------------------------------------------------
// The whole panel's model
// ---------------------------------------------------------------------------

export interface EdgeHealth<T extends KpiTrade = KpiTrade> {
  kpis: Kpis<T>;
  verdict: EdgeVerdict;
  shape: RShape;
  /**
   * ⚠ NULL unless the trader went BOTH ways in the period. A "Short: $0 over 0
   * trades" tile next to a long one reads as a losing side rather than an
   * untraded one — the old client hides the section entirely.
   */
  byDirection: { long: Kpis<T>; short: Kpis<T> } | null;
  weeks: WeekPoint[];
  /** False below `EDGE_WEEK_MIN` — two points are not a trend. */
  trendWorthShowing: boolean;
}

export interface EdgeHealthInput<T extends KpiTrade> {
  /**
   * 🚨 THE FINAL POPULATION. Already narrowed — account selection applied,
   * exclude-from-metrics applied, period applied. The engine adds nothing.
   *
   * ⚠ THIS IS NOT A STYLE NOTE. It shipped wrong: the call site passed the
   * UNNARROWED rows and let `selectKpis` narrow internally, so the tiles counted
   * 509 trades while the R-multiple distribution counted 272 out of a set that
   * still contained every SIM and replay trade — **54 trades that no other
   * figure on the panel could see**. One panel, two populations, and the caption
   * printed them side by side as "272 of 509" as though they were one.
   *
   * Found by the side-by-side against beta, which said 218 of 510. Nothing else
   * could have found it: both numbers are plausible, and 3,969 tests passed.
   */
  trades: readonly T[];
  /**
   * The KPI engine, injected — this module does not decide what a win is.
   *
   * ⚠ It may narrow again; narrowing an already-narrowed set is a no-op. What it
   * must NOT be handed is a wider set than `trades`.
   */
  kpisOf: (ts: readonly T[]) => Kpis<T>;
  rrOf: (t: T) => number | null;
  sideOf: (t: T) => string | null;
  /** ⚠ Takes the TRADE — the caller resolves which date a week is keyed on. */
  weekOf: (t: T) => string | null;
}

export function edgeHealth<T extends KpiTrade & { netPnl: number | null }>(
  input: EdgeHealthInput<T>,
): EdgeHealth<T> | null {
  // ⚠ ONE NAME, READ ONCE. Every section below derives from `trades` and none
  // of them reaches back into `input` — see the note on the field. The defect
  // this guards against was two sections quietly disagreeing about which set
  // they were describing.
  const trades = input.trades;
  const kpis = input.kpisOf(trades);
  // ⚠ NULL, not an empty panel. The host shows its own empty state.
  if (!kpis.count) return null;

  const longs = trades.filter((t) => input.sideOf(t) === 'long');
  const shorts = trades.filter((t) => input.sideOf(t) === 'short');
  const weeks = weeklyExpectancy(trades, input.weekOf);

  return {
    kpis,
    verdict: edgeVerdict(kpis),
    shape: rShape(trades.map(input.rrOf)),
    byDirection: (longs.length && shorts.length)
      ? { long: input.kpisOf(longs), short: input.kpisOf(shorts) }
      : null,
    weeks,
    trendWorthShowing: weeks.length >= EDGE_WEEK_MIN,
  };
}
